import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BarChart3,
  ChevronRight,
  LayoutDashboard,
  Lock,
  LogOut,
  MapPin,
  Menu,
  Plus,
  ScanLine,
  Settings,
  ShieldCheck,
  Shirt,
  ShoppingBag,
  Tag,
  Upload,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { usePlan } from "@/lib/use-plan";
import { workspacesApi, WorkspaceSummary } from "@/lib/catalog-api";
import { toast } from "sonner";
import TrialBanner from "@/components/TrialBanner";
import ReadOnlyBanner from "@/components/ReadOnlyBanner";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/brand/Wordmark";

interface NavItem {
  to: string;
  label: string;
  icon: any;
  ready: boolean;
  end?: boolean;
  feature?: string;
}

function trialDaysLeft(trialEndsAt?: string | null) {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86400000));
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

export default function AppShell() {
  const { user, workspace, logout, resendVerification } = useAuth();
  const { features, effectivePlan, readOnly } = usePlan();
  const navigate = useNavigate();
  const [comingSoon, setComingSoon] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const days = useMemo(() => trialDaysLeft(workspace?.trial_ends_at), [workspace?.trial_ends_at]);

  if (!user || !workspace) return null;

  const isAdmin = user.role === "owner" || user.role === "admin";
  const NAV: NavItem[] = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, ready: true, end: true },
    { to: "/app/items", label: "Items", icon: Tag, ready: true },
    { to: "/app/scan", label: "Scan", icon: ScanLine, ready: true, feature: "scan" },
    { to: "/app/outfits", label: "Outfits", icon: Shirt, ready: true, feature: "outfits" },
    { to: "/app/loans", label: "Loans", icon: ShoppingBag, ready: true, feature: "loans" },
    { to: "/app/locations", label: "Locations", icon: MapPin, ready: true },
    { to: "/app/reports", label: "Reports", icon: BarChart3, ready: isAdmin, feature: "reports" },
    ...(isAdmin
      ? [
          { to: "/app/import", label: "Import", icon: Upload, ready: true, feature: "csv_import" },
          { to: "/app/admin", label: "Admin", icon: ShieldCheck, ready: true, end: true },
        ]
      : []),
    { to: "/app/settings", label: "Settings", icon: Settings, ready: true },
  ];

  const showVerifyBanner = !user.email_verified && !bannerDismissed;

  async function handleSignOut() {
    navigate("/", { replace: true });
    await logout();
  }
  async function onResend() {
    setResending(true); setResendMessage(null);
    try { await resendVerification(); setResendMessage("Verification email sent."); }
    catch (e: any) { setResendMessage(e?.message || "Could not send."); }
    finally { setResending(false); }
  }

  return (
    <div data-testid="app-shell" className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden md:flex flex-col w-60 border-r border-border bg-bone/40 sticky top-0 h-screen shrink-0">
        <Link to="/" data-testid="app-sidebar-wordmark" className="px-6 h-16 flex items-center font-display text-lg tracking-tight border-b border-border">
          <Wordmark className="h-9" />
        </Link>
        <div className="px-4 py-4 border-b border-border">
          <Button asChild data-testid="sidebar-catalog-btn" className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 text-sm">
            <Link to="/app/items/new"><Plus className="w-4 h-4 mr-2" />Catalog item</Link>
          </Button>
        </div>
        <nav className="flex-1 py-2">
          {NAV.map((item) => {
            const locked = !!(item.feature && !features[item.feature]);
            return item.ready ? (
              <NavLink key={item.to} to={item.to} end={item.end ?? false}
                data-testid={`app-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "text-oxblood font-medium bg-oxblood/5 border-l-2 border-oxblood"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03] border-l-2 border-transparent"
                  }`}>
                <item.icon className="w-4 h-4" strokeWidth={1.6} />
                {item.label}
                {locked && (
                  <Lock className="w-3 h-3 ml-auto text-muted-foreground/70" strokeWidth={1.8}
                    aria-label="Pro feature" />
                )}
              </NavLink>
            ) : (
              <button key={item.to} type="button"
                data-testid={`app-nav-${item.label.toLowerCase()}`}
                onClick={() => setComingSoon(item.label)}
                className="w-full flex items-center gap-3 px-6 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-foreground/[0.03] border-l-2 border-transparent transition-colors">
                <item.icon className="w-4 h-4" strokeWidth={1.6} />
                {item.label}
                <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">soon</span>
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-border">
          <WorkspaceSwitcher currentName={workspace.name} />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 border-b border-border bg-background/85 backdrop-blur-md flex items-center justify-between px-6 md:px-10">
          <div className="flex items-center gap-4">
            <Link to="/app" data-testid="app-mobile-wordmark" className="md:hidden"><Wordmark className="h-7" /></Link>
          </div>
          <div className="flex items-center gap-5">
            {days !== null && effectivePlan === "trial" && !readOnly && (
              <span data-testid="app-trial-countdown" className="hidden sm:inline-flex items-center gap-2 text-xs text-foreground/80 border border-border px-3 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-oxblood" />
                {days} day{days === 1 ? "" : "s"} left in trial
              </span>
            )}
            <UserMenu name={user.full_name} email={user.email} onSignOut={handleSignOut} />
          </div>
        </header>

        {showVerifyBanner && (
          <div data-testid="verify-email-banner" role="status"
            className="bg-oxblood/10 border-b border-oxblood/20 px-6 md:px-10 py-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="text-foreground">Verify your email to secure your account.</span>
            <button type="button" onClick={onResend} disabled={resending}
              data-testid="verify-banner-resend"
              className="text-oxblood font-medium underline underline-offset-4 hover:text-oxblood-deep disabled:opacity-50">
              {resending ? "Sending…" : "Resend"}
            </button>
            {resendMessage && <span className="text-xs text-muted-foreground">{resendMessage}</span>}
            <button type="button" onClick={() => setBannerDismissed(true)} data-testid="verify-banner-dismiss" aria-label="Dismiss" className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <ReadOnlyBanner />
        <TrialBanner />

        <div className="pb-20 md:pb-0">
          <Outlet />
        </div>
      </div>

      <MobileNav nav={NAV} features={features} onComingSoon={setComingSoon} onSignOut={handleSignOut} workspaceName={workspace.name} />

      {comingSoon && <ComingSoon name={comingSoon} onClose={() => setComingSoon(null)} />}
    </div>
  );
}

function UserMenu({ name, email, onSignOut }: { name: string; email: string; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" data-testid="app-user-menu-trigger" onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 hover:opacity-80 transition-opacity">
        <span className="w-9 h-9 grid place-items-center bg-oxblood text-ivory text-xs font-medium tracking-wider">{initials(name)}</span>
        <ChevronRight className="hidden md:block w-3 h-3 text-muted-foreground rotate-90" />
      </button>
      {open && (<>
        <button type="button" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 z-30" />
        <div data-testid="app-user-menu" className="absolute right-0 mt-3 w-64 bg-background border border-border z-40">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium truncate">{name}</p>
            <p className="text-xs text-muted-foreground truncate">{email}</p>
          </div>
          <button type="button" data-testid="app-signout"
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground hover:bg-bone/60 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </>)}
    </div>
  );
}

function ComingSoon({ name, onClose }: { name: string; onClose: () => void }) {
  return (
    <div data-testid="coming-soon-modal" role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-charcoal/40 backdrop-blur-sm px-6">
      <div className="bg-background border border-border max-w-sm w-full p-6">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Coming soon</p>
        <h3 className="font-display text-2xl">{name}</h3>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          {`${name} ships in the next update. We're rolling features out one at a time so each one actually works in a working wardrobe.`}
        </p>
        <Button onClick={onClose} data-testid="coming-soon-close" className="mt-6 w-full bg-foreground hover:bg-charcoal text-ivory rounded-none h-11">Got it</Button>
      </div>
    </div>
  );
}


function WorkspaceSwitcher({ currentName }: { currentName: string }) {
  const [open, setOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    workspacesApi.list().then((r) => setWorkspaces(r.workspaces)).catch(() => {});
  }, []);

  async function goTo(id: string) {
    setBusy(true);
    try {
      await workspacesApi.switch(id);
      window.location.assign("/app");
    } catch {
      toast.error("Could not switch workspace.");
      setBusy(false);
    }
  }

  async function addWorkspace() {
    const name = window.prompt("New workspace name (a fresh, empty wardrobe will be created):");
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      await workspacesApi.create(name.trim());
      window.location.assign("/app");
    } catch {
      toast.error("Could not create workspace.");
      setBusy(false);
    }
  }

  return (
    <div className="relative">
      <button type="button" data-testid="workspace-switcher"
        onClick={() => setOpen((o) => !o)} disabled={busy}
        className="w-full flex items-center justify-between gap-2 text-left hover:bg-bone/60 transition-colors px-2 py-1.5 rounded">
        <span className="min-w-0">
          <span className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Workspace</span>
          <span className="block text-foreground text-sm font-medium truncate">{currentName}</span>
        </span>
        <ChevronRight className="w-4 h-4 shrink-0 -rotate-90 text-muted-foreground" />
      </button>
      {open && (<>
        <button type="button" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 z-30" />
        <div data-testid="workspace-switcher-menu" className="absolute left-0 bottom-full mb-2 w-60 bg-background border border-border z-40 shadow-lg">
          <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Workspaces</div>
          <div className="max-h-72 overflow-auto py-1">
            {workspaces.map((w) => (
              <button key={w.id} type="button"
                onClick={() => goTo(w.id)}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-bone/60 transition-colors truncate ${w.name === currentName ? "text-oxblood font-medium" : "text-foreground"}`}>
                {w.name}
              </button>
            ))}
          </div>
          <button type="button" data-testid="add-workspace-btn" onClick={addWorkspace}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm border-t border-border text-foreground hover:bg-bone/60 transition-colors">
            <Plus className="w-4 h-4 text-oxblood" /> New workspace
          </button>
        </div>
      </>)}
    </div>
  );
}

function MobileNav({ nav, features, onComingSoon, onSignOut, workspaceName }: {
  nav: NavItem[]; features: Record<string, boolean>;
  onComingSoon: (name: string) => void; onSignOut: () => void; workspaceName: string;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const tabBase = "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] tracking-wide py-2";
  const tab = ({ isActive }: { isActive: boolean }) =>
    `${tabBase} ${isActive ? "text-oxblood" : "text-muted-foreground"}`;

  return (
    <>
      <nav data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-background border-t border-border flex items-stretch">
        <NavLink to="/app" end className={tab} data-testid="mobilenav-dashboard">
          <LayoutDashboard className="w-5 h-5" strokeWidth={1.6} /> Home
        </NavLink>
        <NavLink to="/app/items" className={tab} data-testid="mobilenav-items">
          <Tag className="w-5 h-5" strokeWidth={1.6} /> Items
        </NavLink>
        <Link to="/app/items/new" data-testid="mobilenav-add"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] text-ivory">
          <span className="w-11 h-11 -mt-5 grid place-items-center bg-oxblood rounded-full shadow-lg border-4 border-background">
            <Plus className="w-5 h-5" strokeWidth={2} />
          </span>
          <span className="text-oxblood">Add</span>
        </Link>
        <NavLink to="/app/scan" className={tab} data-testid="mobilenav-scan">
          <ScanLine className="w-5 h-5" strokeWidth={1.6} /> Scan
        </NavLink>
        <button type="button" onClick={() => setMoreOpen(true)} className={`${tabBase} text-muted-foreground`} data-testid="mobilenav-more">
          <Menu className="w-5 h-5" strokeWidth={1.6} /> More
        </button>
      </nav>

      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-more-sheet">
          <button type="button" aria-label="Close" onClick={() => setMoreOpen(false)} className="absolute inset-0 bg-charcoal/40" />
          <div className="absolute bottom-0 inset-x-0 bg-background border-t border-border rounded-t-2xl max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Workspace</p>
                <p className="text-sm font-medium truncate max-w-[220px]">{workspaceName}</p>
              </div>
              <button type="button" onClick={() => setMoreOpen(false)} aria-label="Close" className="text-muted-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="py-2">
              {nav.map((item) => {
                const locked = !!(item.feature && !features[item.feature]);
                return item.ready ? (
                  <NavLink key={item.to} to={item.to} end={item.end ?? false}
                    onClick={() => setMoreOpen(false)}
                    data-testid={`mobilenav-more-${item.label.toLowerCase()}`}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm ${isActive ? "text-oxblood font-medium" : "text-foreground"}`}>
                    <item.icon className="w-4 h-4" strokeWidth={1.6} /> {item.label}
                    {locked && <Lock className="w-3 h-3 ml-auto text-muted-foreground/70" />}
                  </NavLink>
                ) : (
                  <button key={item.to} type="button"
                    onClick={() => { setMoreOpen(false); onComingSoon(item.label); }}
                    className="w-full flex items-center gap-3 px-5 py-3 text-sm text-muted-foreground">
                    <item.icon className="w-4 h-4" strokeWidth={1.6} /> {item.label}
                    <span className="ml-auto text-[10px] uppercase tracking-[0.18em]">soon</span>
                  </button>
                );
              })}
              <Link to="/app/settings" onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-sm text-foreground" data-testid="mobilenav-more-settings">
                <Settings className="w-4 h-4" strokeWidth={1.6} /> Settings
              </Link>
              <button type="button" onClick={() => { setMoreOpen(false); onSignOut(); }}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm text-oxblood border-t border-border mt-2">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

